/**
 * Copyright © 2026 Adnuntius AS.
 */
import {BsDatepickerConfig, BsLocaleService} from 'ngx-bootstrap/datepicker';
import {ChangeDetectionStrategy, Component, Inject, Input, OnDestroy, OnInit, signal} from '@angular/core';
import {Subscription} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {FormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {BsDatepickerModule} from 'ngx-bootstrap/datepicker';
import {TabsModule} from 'ngx-bootstrap/tabs';
import * as _ from 'lodash';
import * as moment from 'moment';

import template from './exploration.component.html';
import {applyNetworkCalendarWeekStart, toBsDateInputFormat} from './form-controls/bs-datepicker-locale';
import {pickerDateFromApiInstant} from '../locale/network-date-picker-utils';
import {LocaleNumberPipe} from '../filters/locale-number.pipe';
import {AppLocaleService} from '../locale/app-locale.service';
import {getIntlLocaleData} from '../locale/intl-locale-data';
import {convertIabCategoryToString} from '../../../components/util/iab-taxonomy';
import {AdnTimeShifterService} from '../../../components/util/adn-time-shifter.service';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {deferSignalStateWrite} from '../util/template-state';
import {TrafficHistogramApiService} from './traffic-histogram-api.service';
import {APP_SHARED} from '../app-shared';
import {ExplorationTableComponent} from './exploration-table.component';

interface ExplorerType {
  value: string;
  title: string;
  chartData: string;
  filterId: string;
  filterName?: boolean;
}

@Component({
  standalone: true,
  imports: [...APP_SHARED, ExplorationTableComponent, FormsModule, TranslateModule, BsDatepickerModule, TabsModule],
  selector: 'adn-exploration-component',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExplorationComponent implements OnInit, OnDestroy {
  @Input() obj: any;
  @Input() param = 'adUnit';
  @Input() networkWide = false;
  @Input() suppressAdUnitBreakdown = false;

  explorerTypes: ExplorerType[] = [
    {value: 'SEGMENT', title: 'Segment', chartData: 'segments', filterId: 'userSegment', filterName: true},
    {value: 'CATEGORY', title: 'Category', chartData: 'categories', filterId: 'category'},
    {value: 'KEYWORD', title: 'Keyword', chartData: 'keywords', filterId: 'keyword'},
    {value: 'KEY_VALUES', title: 'Key Value', chartData: 'keyValues', filterId: 'keyValue'},
    {value: 'COUNTRY', title: 'Country', chartData: 'countries', filterId: 'country'},
    {value: 'REGION', title: 'Region', chartData: 'regions', filterId: 'region'},
    {value: 'CITY', title: 'City', chartData: 'cities', filterId: 'city'},
    {value: 'POSTCODE', title: 'Postcode', chartData: 'postcodes', filterId: 'postcode'},
    {value: 'DEVICE', title: 'Device', chartData: 'devices', filterId: 'device'},
    {value: 'CREATIVE_SIZE', title: 'Creative Size', chartData: 'creativeSizes', filterId: 'creativeSize'},
    {value: 'CREATIVE_TYPE', title: 'Creative Type', chartData: 'creativeTypes', filterId: 'creativeType'}
  ];
  selectedTrafficTypes: string[] = _.map(this.explorerTypes, 'value');

  startDate: moment.Moment;
  endDate: moment.Moment;
  modStartDate: Date;
  modEndDate: Date;
  showAll = false;
  hasFilter = false;
  hasUpdatedFilter = false;
  hasRequested = false;
  firstRunThrough = false;
  selectedTile = 'segments';
  customFilterType: string;
  customFilter: string;
  bsConfig: Partial<BsDatepickerConfig>;
  readonly exploration = signal<any>({
    completed: true,
    error: false
  });

  readonly boundUpdateFilter = (type: string, value: any): void => this.updateFilter(type, value);

  [key: string]: any;

  private explorationSubscription?: Subscription;
  private requestVersion = 0;
  private destroyed = false;

  constructor(
    private readonly trafficHistogramApi: TrafficHistogramApiService,
    private readonly bsLocaleService: BsLocaleService,
    private readonly translate: TranslateService,
    private readonly localeNumberPipe: LocaleNumberPipe,
    private readonly appLocale: AppLocaleService,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(AdnTimeShifterService) private readonly adnTimeShifter: any
  ) {
    this.localeData = getIntlLocaleData(this.appLocale.getLocaleId());
    this.bsConfig = {
      dateInputFormat: toBsDateInputFormat(this.localeData.DATETIME_FORMATS.shortDate),
      showTodayButton: true,
      showClearButton: true,
      todayPosition: 'left',
      clearPosition: 'right',
      todayButtonLabel: this.translate.instant('button.today'),
      clearButtonLabel: this.translate.instant('button.clear')
    };
  }

  ngOnInit(): void {
    applyNetworkCalendarWeekStart(
      this.bsLocaleService,
      this.LocalNetworkProfile.getDefaults()?.startDayCalendar,
      this.localeData
    );

    const networkToday = this.adnTimeShifter.getMomentInNetworkTimezone().startOf('day');
    this.startDate = networkToday.clone();
    this.endDate = networkToday.clone().add(7, 'days');
    this.modStartDate = pickerDateFromApiInstant(networkToday.toDate(), this.adnTimeShifter);
    this.modEndDate = pickerDateFromApiInstant(this.endDate.toDate(), this.adnTimeShifter);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.requestVersion++;
    this.explorationSubscription?.unsubscribe();
  }

  clearExplorerFilter(): void {
    _.forEach(this.explorerTypes, (type) => {
      if (type.filterName) {
        _.set(this, type.filterName as any, null);
        _.set(this, (type.filterName as any) + 'AsString', null);
        _.set(this, (type.filterName as any) + 'AsStringName', null);
      }
      if (type.filterId) {
        _.set(this, type.filterId, null);
        _.set(this, type.filterId + 'AsString', null);
        _.set(this, type.filterId + 'AsStringName', null);
      }
    });
    this.hasFilter = false;
  }

  updateFilter(type: string, value: any): void {
    this.hasUpdatedFilter = true;
    _.forEach(this.explorerTypes, (t) => {
      if (t.value === type) {
        const id = _.get(value, 'id');
        const name = _.get(value, 'name');
        let filterValue;
        if (id && t.filterId) {
          this[t.filterId] = this[t.filterId] || [];
          filterValue = id;
          if (name && t.filterName) {
            filterValue = {id: filterValue, name: name};
          }
          const findMatch = _.find(this[t.filterId], (entry: any) => {
            return entry === filterValue || (!!entry.id && entry.id === filterValue.id);
          });
          if (findMatch) {
            return;
          }
          this[t.filterId].push(filterValue);
          this[t.filterId + 'AsString'] = filterValue.id ? _.map(this[t.filterId], (f: any) => {
            return f.id;
          }).join(', ') : this[t.filterId].join(', ');
          this[t.filterId + 'AsStringName'] = filterValue.name ? _.map(this[t.filterId], (f: any) => {
            return f.name;
          }).join(', ') : '';
          this.hasFilter = true;
        }
      }
    });
  }

  updateCustomFilter(): void {
    this.updateFilter(this.customFilterType, {id: this.customFilter});
    this.customFilter = null;
  }

  filterTextChanged(): void {
    this.hasFilter = _.some(this.explorerTypes, (type) => {
      const rawValue = _.get(this, type.filterId + 'AsString');
      return _.isString(rawValue) && _.some(rawValue.split(','), value => _.trim(value).length > 0);
    });
  }

  headingBadge(count: number): string | number {
    return count < 50 ? count : '50+';
  }

  isTypeSelected(type: string): boolean {
    return this.selectedTrafficTypes.indexOf(type) > -1;
  }

  trafficTypesChanged(): void {
    const currentType = _.find(this.explorerTypes, {chartData: this.selectedTile});
    if (!currentType || !this.isTypeSelected(currentType.value)) {
      this.selectedTile = _.get(_.find(this.explorerTypes, (type) => this.isTypeSelected(type.value)), 'chartData', '');
    }
  }

  exploreAdUnit(): void {
    if ((!this.networkWide && !this.obj?.id) || !this.selectedTrafficTypes.length) {
      return;
    }

    const filter: Record<string, string[]> = {};
    _.forEach(this.explorerTypes, (type) => {
      const rawValue = _.get(this, type.filterId + 'AsString', '');
      const elements = _.filter(_.map((_.isString(rawValue) ? rawValue : '').split(','), (s) => {
        return _.trim(s);
      }), (s) => {
        return _.isString(s) && s.length > 0;
      });
      const filterValue = elements.length && elements[0] !== '' ? elements : null;
      if (filterValue) {
        _.set(filter, type.value, _.map(filterValue, (item) => {
          return _.get(item, 'id') || item;
        }));
      }
    });
    const requestHasFilter = !_.isEmpty(filter);
    this.hasFilter = requestHasFilter;

    this.hasRequested = true;
    this.exploration.set({
      error: false,
      completed: false,
      hasFilter: requestHasFilter
    });

    const dates = {startDate: null, endDate: null};
    if (_.isDate(this.modStartDate) && _.isDate(this.modEndDate)) {
      const adjustedDates = this.adnTimeShifter.startAndEndOfDays(
        this.modStartDate,
        this.modEndDate
      );
      dates.startDate = adjustedDates.startDate;
      dates.endDate = adjustedDates.endDate;
    }

    const requestVersion = ++this.requestVersion;
    this.explorationSubscription?.unsubscribe();
    this.explorationSubscription = this.trafficHistogramApi.get(
      this.networkWide ? null : this.obj.id,
      this.networkWide ? null : (this.param || 'adUnit'),
      this.selectedTrafficTypes,
      filter,
      dates.startDate,
      dates.endDate,
      this.suppressAdUnitBreakdown
    ).subscribe({
      next: (data) => {
      const nextExploration: any = {
        error: false,
        completed: true,
        hasFilter: requestHasFilter
      };
      _.forEach(_.filter(this.explorerTypes, (type) => this.isTypeSelected(type.value)), (type) => {
        const rows = _.map(_.get(data, [type.value, 'histogram']) || [], (entry: any) => {
          if (type.filterName) {
            const object = _.get(entry, type.filterId);
            const id = _.get(object, 'id');
            const name = _.get(object, 'name');
            return {name: convertIabCategoryToString(name), y: entry.frequency, id: id, nextWeek: entry.expectedNextWeek};
          }
          return {name: convertIabCategoryToString(entry.value), y: entry.frequency, id: entry.value, nextWeek: entry.expectedNextWeek};
        });
        nextExploration[type.chartData] = rows;

        const chartData = _.map(rows, (row: any) => {
          return {
            name: row.name,
            y: parseFloat(this.localeNumberPipe.transform(row.y, '1.0-0'))
          };
        });
        nextExploration[type.chartData].statsChart = (!chartData || !chartData.length) ? null : {
          chart: {
            type: 'bar',
            zoomType: 'x',
            showAxes: true,
            height: Math.min(100 + chartData.length * 20, 1000)
          },
          plotOptions: {
            series: {
              colorByPoint: true,
              turboThreshold: 2000
            }
          },
          legend: {
            enabled: false
          },
          tooltip: {
            formatter: function() {
              return this.x + '<br><strong>' + this.y + '%</strong>';
            }
          },
          xAxis: {
            categories: _.map(chartData, 'name')
          },
          yAxis: {
            min: 0,
            max: 100,
            title: {
              text: 'Presence in Traffic Samples (%)'
            }
          },
          series: [{
            name: 'Presence (%)',
            data: _.map(chartData, 'y')
          }],
          title: {
            text: null
          },
          credits: {
            enabled: false
          },
          exporting: {
            enabled: true
          }
        };
      });

      let selectedTile = this.selectedTile;
      _.forEach(nextExploration, (type, key) => {
        if (_.isArray(type) && type.length > 0) {
          selectedTile = selectedTile || key;
        }
      });
      if (!selectedTile) {
        selectedTile = 'segments';
      }

      deferSignalStateWrite(() => {
        if (this.destroyed || requestVersion !== this.requestVersion) {
          return;
        }
        this.firstRunThrough = true;
        this.selectedTile = selectedTile;
        this.exploration.set(nextExploration);
      });
      },
      error: () => {
        deferSignalStateWrite(() => {
          if (this.destroyed || requestVersion !== this.requestVersion) {
            return;
          }
          this.exploration.set({
            error: true,
            completed: true,
            hasFilter: requestHasFilter
          });
        });
      }
    });
  }

}
